// Feb 2026 (v6.1) — Admin-only maintenance tool that enables the
// `criticalEmailBackup` preference for every registered user with a stored
// email. Dry-run → confirm → apply. Idempotent; safe to re-run because the
// filter only targets users whose value is currently missing/null/false.

import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../App';
import { ShieldCheck, Loader2, AlertTriangle, Check, ChevronDown, ChevronRight, History } from 'lucide-react';

const SafetyEmailBackupBackfillPanel = () => {
  const [expanded, setExpanded] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | previewing | preview | confirming | applying | done | error
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await axiosInstance.get(
        '/admin/notifications/backfill-critical-email-backup/history'
      );
      setHistory(r.data?.runs || []);
    } catch (_) {
      // history is best-effort — silent failure
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (expanded) loadHistory();
  }, [expanded, loadHistory]);

  const runDryRun = async () => {
    setError(null);
    setApplied(null);
    setPhase('previewing');
    try {
      const r = await axiosInstance.post(
        '/admin/notifications/backfill-critical-email-backup',
        { dryRun: true }
      );
      setPreview(r.data);
      setPhase(r.data?.targetCount > 0 ? 'preview' : 'done');
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Preview failed');
      setPhase('error');
    }
  };

  const apply = async () => {
    setError(null);
    setPhase('applying');
    try {
      const r = await axiosInstance.post(
        '/admin/notifications/backfill-critical-email-backup',
        { dryRun: false }
      );
      setApplied(r.data);
      setPhase('done');
      loadHistory();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Apply failed');
      setPhase('error');
    }
  };

  const reset = () => {
    setPhase('idle');
    setPreview(null);
    setApplied(null);
    setError(null);
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-4 mb-4"
      data-testid="safety-email-backup-backfill-panel"
    >
      <button
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="safety-email-backup-toggle"
      >
        <ShieldCheck className="h-5 w-5 text-emerald-700" />
        <div className="flex-1">
          <div className="font-semibold text-slate-900">Safety Email Backup Backfill</div>
          <div className="text-xs text-slate-500">
            Enables email backup for <strong>Official Community Safety Alerts only</strong>. Does not
            change any user's marketing email preferences.
          </div>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          {phase === 'idle' && (
            <button
              onClick={runDryRun}
              className="w-full px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-md text-sm font-medium flex items-center justify-center gap-2"
              data-testid="safety-email-backup-dry-run-btn"
            >
              <ShieldCheck className="h-4 w-4" />
              Preview affected users (dry-run)
            </button>
          )}

          {phase === 'previewing' && (
            <div className="flex items-center gap-2 text-sm text-slate-600 py-3" data-testid="safety-email-backup-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              Running dry-run…
            </div>
          )}

          {phase === 'preview' && preview && (
            <div className="space-y-3" data-testid="safety-email-backup-preview">
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <div className="font-semibold text-amber-900">
                      This will update <span data-testid="safety-email-backup-target-count">{preview.targetCount}</span> user account{preview.targetCount === 1 ? '' : 's'}.
                    </div>
                    <div className="text-xs text-amber-800 mt-1">
                      This enables email backup for <strong>Official Community Safety Alerts only</strong>.
                      It does <strong>not</strong> enable marketing emails, newsletters, event promotions
                      or commercial communications. Users' marketing email preferences remain unchanged.
                      Safe to re-run — only accounts where the value is currently off or unset will be
                      touched.
                    </div>
                  </div>
                </div>
              </div>

              {preview.sample?.length > 0 && (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700">
                    Sample (first {preview.sample.length} of {preview.targetCount})
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr className="text-left text-slate-500">
                          <th className="px-3 py-1.5 font-medium">Email</th>
                          <th className="px-3 py-1.5 font-medium">Current backup</th>
                          <th className="px-3 py-1.5 font-medium">Email opt-in</th>
                        </tr>
                      </thead>
                      <tbody data-testid="safety-email-backup-sample-body">
                        {preview.sample.map((u) => (
                          <tr key={u.id} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 font-mono text-slate-800">{u.email}</td>
                            <td className="px-3 py-1.5 text-slate-500">{String(u.currentValue ?? 'unset')}</td>
                            <td className="px-3 py-1.5 text-slate-500">{String(u.emailEnabled ?? 'unset')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => setPhase('confirming')}
                  className="flex-1 px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-md text-sm font-medium"
                  data-testid="safety-email-backup-apply-btn"
                >
                  Apply to all {preview.targetCount} users
                </button>
                <button
                  onClick={reset}
                  className="px-4 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-md text-sm"
                  data-testid="safety-email-backup-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {phase === 'confirming' && preview && (
            <div className="bg-red-50 border border-red-300 rounded-md p-3 space-y-3" data-testid="safety-email-backup-confirm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-700 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-900">
                  <div className="font-semibold">Confirm backfill</div>
                  <div className="text-xs mt-1" data-testid="safety-email-backup-confirm-copy">
                    This will enable email backup for <strong>Official Community Safety Alerts</strong> for{' '}
                    <span className="font-bold">{preview.targetCount}</span> user{preview.targetCount === 1 ? '' : 's'}.
                    Marketing email preferences will <strong>not</strong> be changed. Continue?
                  </div>
                  <div className="text-[11px] mt-1.5 text-red-800/80">
                    This action is logged with your admin identity and timestamp.
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={apply}
                  className="flex-1 px-4 py-2.5 bg-red-700 hover:bg-red-800 text-white rounded-md text-sm font-medium"
                  data-testid="safety-email-backup-confirm-btn"
                >
                  Yes, apply now
                </button>
                <button
                  onClick={() => setPhase('preview')}
                  className="px-4 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-md text-sm"
                  data-testid="safety-email-backup-back-btn"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {phase === 'applying' && (
            <div className="flex items-center gap-2 text-sm text-slate-600 py-3" data-testid="safety-email-backup-applying">
              <Loader2 className="h-4 w-4 animate-spin" />
              Applying…
            </div>
          )}

          {phase === 'done' && applied && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-md p-3" data-testid="safety-email-backup-done">
              <div className="flex items-start gap-2">
                <Check className="h-4 w-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-emerald-900">
                  <div className="font-semibold">
                    Updated <span data-testid="safety-email-backup-modified">{applied.modified}</span> of {applied.targetCount} accounts.
                  </div>
                  <div className="text-xs mt-1 text-emerald-800">
                    Ran by <span className="font-mono">{applied.adminEmail || 'admin'}</span> at{' '}
                    {applied.at ? new Date(applied.at).toLocaleString() : 'now'}.
                  </div>
                </div>
              </div>
              <button
                onClick={reset}
                className="mt-3 text-xs text-emerald-800 underline"
                data-testid="safety-email-backup-run-again-btn"
              >
                Run again
              </button>
            </div>
          )}

          {phase === 'done' && !applied && preview && preview.targetCount === 0 && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-md p-3" data-testid="safety-email-backup-nothing-to-do">
              <div className="flex items-start gap-2">
                <Check className="h-4 w-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-emerald-900">
                  <div className="font-semibold">Nothing to do.</div>
                  <div className="text-xs mt-1 text-emerald-800">
                    Every registered user with an email already has safety email backup enabled.
                  </div>
                </div>
              </div>
              <button onClick={reset} className="mt-3 text-xs text-emerald-800 underline" data-testid="safety-email-backup-close-btn">
                Close
              </button>
            </div>
          )}

          {phase === 'error' && (
            <div className="bg-red-50 border border-red-300 rounded-md p-3" data-testid="safety-email-backup-error">
              <div className="text-sm text-red-800">
                <div className="font-semibold">Something went wrong</div>
                <div className="text-xs mt-1 font-mono">{error}</div>
              </div>
              <button onClick={reset} className="mt-3 text-xs text-red-800 underline" data-testid="safety-email-backup-retry-btn">
                Retry
              </button>
            </div>
          )}

          <div className="border-t border-slate-200 pt-3 mt-3">
            <div className="flex items-center gap-1.5 mb-2">
              <History className="h-3.5 w-3.5 text-slate-500" />
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Run history</span>
            </div>
            {historyLoading ? (
              <div className="text-xs text-slate-500 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </div>
            ) : history.length === 0 ? (
              <div className="text-xs text-slate-500">No previous runs recorded.</div>
            ) : (
              <ul className="text-xs space-y-1" data-testid="safety-email-backup-history">
                {history.map((h) => (
                  <li key={h.id} className="text-slate-700 font-mono">
                    <span className="text-slate-500">{h.at ? new Date(h.at).toLocaleString() : '—'}</span>
                    {' · '}
                    <span>{h.adminEmail || h.adminId || 'admin'}</span>
                    {' · '}
                    <span className="text-emerald-700">{h.meta?.modified ?? 0}</span>
                    <span className="text-slate-400">/{h.meta?.targetCount ?? 0} updated</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SafetyEmailBackupBackfillPanel;
